<template>
  <div class="organization-input dadata-field" v-click-outside="handleClickOutside">
    <input
      class="organization-input__input"
      :class="inputClasses"
      :id="id"
      type="text"
      :placeholder="placeholder"
      :value="inputValue"
      :disabled="disabled"
      @input="handleNativeInput"
      @focus="handleFocus"
    />

    <transition name="fade">
      <div v-if="isOpened" class="organization-input__dropdown">
        <div
          v-if="isPending"
          class="organization-input__text organization-input__text--secondary organization-input__title organization-input__loader"
        >
          Поиск...
        </div>

        <template v-else>
          <template v-if="inputValue.length">
            <ul v-if="options.length" class="organization-input__list">
              <li
                v-for="option in options"
                :key="getOptionKey(option)"
                class="organization-input__list-item"
                @click="handleItemClick(option)"
              >
                <span class="organization-input__text">
                  {{ getOptionTitle(option) }}
                </span>

                <span v-if="option.inn || option.kpp">
                  {{ option.inn && `ИНН ${option.inn};` }}
                  {{ option.kpp && `КПП ${option.kpp}` }}
                </span>

                <span>
                  {{ option.country || locationCountry }},
                  {{ option.region_with_type || locationRegion }}
                </span>

                <span v-if="option.address">
                  {{ option.address }}
                </span>

                <span v-if="option.index">
                  {{ option.index }}
                </span>
              </li>
            </ul>

            <div v-else class="organization-input__text organization-input__title organization-input__text--secondary">
              Ничего не найдено
            </div>
          </template>

          <div v-else class="organization-input__text organization-input__title organization-input__text--secondary">
            Пример: ООО "Сбербанк"
          </div>
        </template>
      </div>
    </transition>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { fetchOrganisationSuggestions } from '@shared/services/dadata/dadata.service'
import { isObject } from '@shared/utils/type'

export default {
  name: 'OrganizationInput',
  props: {
    token: {
      type: String,
      required: true,
    },
    value: {
      type: [Object, String],
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    error: {
      type: Boolean,
      default: false,
    },
    valid: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    location: {
      type: [Object, String],
      default: null,
    },
    id: {
      type: String,
      default: 'organization-input',
    },
    minSearchLength: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      isOpened: false,
      inputValue: '',
      options: [],
      isPending: false,
      isResolvingInitialValue: false,
      lastResolvedStringValue: '',
      requestId: 0,
      debouncedSearch: null,
      isInternalValueUpdate: false,
    }
  },
  computed: {
    inputClasses() {
      return {
        'organization-input__input--error': this.error,
        'organization-input__input--valid': this.valid,
        'organization-input__input--disabled': this.disabled,
      }
    },
    locationCountry() {
      return this.location?.country || ''
    },
    locationRegion() {
      return this.location?.region_with_type || this.location?.region || ''
    },
  },
  created() {
    this.debouncedSearch = debounce(this.searchOptions, 500)
  },
  beforeDestroy() {
    if (this.debouncedSearch?.cancel) {
      this.debouncedSearch.cancel()
    }
  },
  methods: {
    emitValue(value) {
      this.isInternalValueUpdate = true
      this.$emit('update:value', value)

      this.$nextTick(() => {
        this.isInternalValueUpdate = false
      })
    },
    formatValue(value) {
      if (!value) return ''

      if (typeof value === 'string') {
        return value
      }

      const opf = value.opf || ''
      const name = value.name || ''
      const inn = value.inn || ''
      const kpp = value.kpp ? `, ${value.kpp}` : ''

      if (!name && !inn && !kpp) return ''

      return `${opf} "${name}", ${inn}${kpp}`.trim()
    },
    getOptionTitle(option) {
      const opf = option.opf || ''
      const name = option.name || ''

      if (!opf && !name) return ''

      return `${opf} "${name}"`.trim()
    },
    getOptionKey(option) {
      return `${option.inn || ''}-${option.kpp || ''}-${option.name || ''}`
    },
    async syncInputValueFromValue(value) {
      if (!value) {
        this.inputValue = ''
        this.options = []
        this.lastResolvedStringValue = ''
        return
      }

      if (typeof value === 'string') {
        this.inputValue = value
        await this.resolveInitialStringValue(value)
        return
      }

      this.inputValue = this.formatValue(value)
      this.options = [value]
    },
    async resolveInitialStringValue(value) {
      const query = String(value || '').trim()

      if (!query) return
      if (this.lastResolvedStringValue === query) return

      this.lastResolvedStringValue = query
      this.isResolvingInitialValue = true

      try {
        const options = await this.fetchOptions(query)
        const option = options[0] || null

        if (!option) {
          this.options = []
          this.inputValue = query
          return
        }

        this.options = [option]
        this.inputValue = this.formatValue(option)

        this.emitValue(option)
      } catch (error) {
        console.error(error)
        this.options = []
        this.inputValue = query
      } finally {
        this.isResolvingInitialValue = false
      }
    },
    handleNativeInput(event) {
      this.inputValue = event.target.value

      const query = String(this.inputValue || '').trim()

      if (isObject(this.value)) {
        this.emitValue(null)
      }

      if (!query) {
        this.options = []
        this.isPending = false
        return
      }

      if (query.length < this.minSearchLength) {
        this.options = []
        this.isPending = false
        return
      }

      this.isPending = true
      this.debouncedSearch()
    },
    async searchOptions() {
      const query = String(this.inputValue || '').trim()

      if (!query || query.length < this.minSearchLength) {
        this.options = []
        this.isPending = false
        return
      }

      const currentRequestId = ++this.requestId

      try {
        const options = await this.fetchOptions(query)

        if (currentRequestId !== this.requestId) return

        this.options = options
      } catch (error) {
        if (currentRequestId !== this.requestId) return

        console.error(error)
        this.options = []
      } finally {
        if (currentRequestId === this.requestId) {
          this.isPending = false
        }
      }
    },
    fetchOptions(query) {
      return fetchOrganisationSuggestions({
        query,
        token: this.token,
        location: this.location,
      })
    },
    handleFocus(event) {
      this.isOpened = true
      this.$emit('focus', event)
    },
    resetField() {
      this.inputValue = ''
      this.options = []
      this.lastResolvedStringValue = ''
      this.emitValue(null)
    },
    normalizeSelectedOption(option) {
      if (!option) return null

      return {
        ...option,
        city: option.city || this.location?.city || '',
        country: option.country || this.location?.country || '',
        index: option.index || this.location?.index || '',
        region_with_type: option.region_with_type || this.location?.region_with_type || this.location?.region || '',
        region: option.region || this.location?.region || '',
      }
    },
    setCurrentOption(option) {
      if (!option) {
        this.resetField()
        return
      }

      const normalizedOption = this.normalizeSelectedOption(option)

      this.inputValue = this.formatValue(normalizedOption)
      this.options = [normalizedOption]
      this.emitValue(normalizedOption)
    },
    handleClickOutside() {
      if (!this.isOpened) return

      this.setCurrentOption(this.value)
      this.isOpened = false
    },
    handleItemClick(option) {
      this.setCurrentOption(option)
      this.isOpened = false
    },
  },
  watch: {
    value: {
      immediate: true,
      async handler(value) {
        if (this.isInternalValueUpdate) {
          return
        }

        await this.syncInputValueFromValue(value)
      },
    },
    'location.country'(newCountry, oldCountry) {
      if (newCountry === oldCountry) return

      this.resetField()
    },
  },
}
</script>

<style lang="sass">
@import "~@src/legacy/sass/params"

.organization-input
  position: relative
  list-style-type: none

  &__input
    width: 100%
    min-height: 48px
    padding: 0 16px
    border: var(--field-border-width, 1px) solid var(--field-border-color, #C8C8C8)
    border-radius: 4px

    &--error
      border: var(--field-error-border-width, 1px) solid var(--field-error-border-color, $error)

    &--disabled
      opacity: var(--field-opacity-disabled, 0.7)
      pointer-events: none
      background-color: var(--field-disabled-bg-color, #ededed)

    &::placeholder
      color: var(--field-placeholder-color, $gray-text)

  &__dropdown
    position: absolute
    background: #ffffff
    width: 100%
    border-radius: 4px
    max-height: 200px
    scrollbar-width: none
    overflow-y: scroll
    top: calc(100% + 4px)
    padding-block: 12px
    border: 1px solid var(--field-border-color, #C8C8C8)
    box-shadow: 0 4px 12px 0 #0000001A
    z-index: 1

  &__list
    color: #2c2c2c

  &__text
    font-family: Proxima Nova Semi, sans-serif

  &__text--secondary
    color: #a6a6a6

  &__title
    padding: 4px 24px

  &__list-item
    cursor: pointer
    padding: 12px 24px
    transition: 200ms
    transition-property: background
    display: flex
    flex-direction: column

    &:hover
      background: rgba(0, 0, 0, 0.1)

  &__loader
    animation-name: loading-opacity
    animation-duration: 1000ms
    animation-iteration-count: infinite
    animation-timing-function: ease-in-out
    opacity: 1

  &__placeholder
    padding: 12px 24px

@keyframes loading-opacity
  0%
    opacity: 1

  50%
    opacity: 0.2

  100%
    opacity: 1
</style>



// WEBPACK FOOTER //
// src/modules/shared/ui/public/forms/fields/OrganizationInput.vue